import React, { useState } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import { analyzeFiveRuleHouse } from '../../utils/fiveRuleHouseEngine';
import { Shield, Sparkles, AlertCircle, ChevronDown, ChevronUp, CheckCircle, HelpCircle } from 'lucide-react';

interface FiveRuleHouseCardProps {
  kundali: CompleteKundali;
  themeId: LifeThemeId;
  lang: Language;
}

export const FiveRuleHouseCard: React.FC<FiveRuleHouseCardProps> = ({
  kundali,
  themeId,
  lang
}) => {
  const isHi = lang === 'hi';
  const assessment = analyzeFiveRuleHouse(kundali, themeId, lang);
  const [isDeepExpanded, setIsDeepExpanded] = useState<boolean>(false);

  const getBadgeColor = (nature: string) => {
    switch (nature) {
      case 'POSITIVE':
        return 'bg-emerald-50 text-emerald-800 border-emerald-200';
      case 'NEGATIVE':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'MIXED':
        return 'bg-blue-50 text-blue-800 border-blue-200';
      default:
        return 'bg-astro-secondary-surface text-astro-text border-astro-border';
    }
  };

  return (
    <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-6 shadow-xs space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-astro-border/80 pb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider bg-astro-primary/10 text-astro-primary px-2.5 py-0.5 rounded-full border border-astro-primary/20">
              {isHi ? 'पंच-सूत्रीय भाव विश्लेषण' : 'Five-Rule House Assessment'}
            </span>
            <span className="text-xs text-[#6B7280]">
              {isHi ? `भाव ${assessment.houseNumber} (${assessment.houseSign})` : `House ${assessment.houseNumber} (${assessment.houseSign})`}
            </span>
          </div>
          <h3 className="text-xl font-bold font-serif text-astro-text mt-1">
            {isHi ? assessment.dualTerminology.clientPromiseHi : assessment.dualTerminology.clientPromise}
          </h3>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase font-bold text-[#6B7280]">
              {isHi ? 'कुल सामर्थ्य अंक' : 'Net Promise Score'}
            </div>
            <div className="text-2xl font-black text-astro-primary">
              {assessment.netPromiseScore}<span className="text-sm font-normal text-[#6B7280]">/100</span>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-xl text-xs font-bold border ${getBadgeColor(assessment.netAssessment)}`}>
            {assessment.netAssessment}
          </div>
        </div>
      </div>

      {/* Dual Terminology Banner */}
      <div className="bg-[#FAF6ED] rounded-xl p-3.5 border border-astro-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs">
        <div>
          <span className="font-bold text-astro-primary">{isHi ? 'ज्योतिषीय योग:' : 'Astrological Alignment:'} </span>
          <span className="text-astro-secondary-text">{isHi ? assessment.dualTerminology.technicalAstrologyHi : assessment.dualTerminology.technicalAstrology}</span>
        </div>
        <div className="shrink-0 text-[11px] text-astro-accent font-semibold">
          {isHi ? 'भावेश:' : 'Lord:'} <strong>{assessment.houseLord}</strong> • {isHi ? 'कारक:' : 'Karaka:'} <strong>{assessment.primaryKaraka}</strong>
        </div>
      </div>

      {/* Five Rules List */}
      <div className="space-y-2.5">
        <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">
          {isHi ? 'पंच-सूत्रीय कारक विवरण' : 'Five Evaluation Factors'}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {assessment.factors.map((f) => (
            <div
              key={f.ruleNumber}
              className={`p-3.5 rounded-xl border transition-all ${
                f.scoreContribution > 0
                  ? 'bg-emerald-50/40 border-emerald-200/80 text-emerald-950'
                  : f.scoreContribution < 0
                  ? 'bg-amber-50/40 border-amber-200/80 text-amber-950'
                  : 'bg-white border-astro-border text-astro-text'
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-1.5 font-bold text-xs">
                  <span className="w-4 h-4 rounded-full bg-astro-primary/10 text-astro-primary text-[10px] flex items-center justify-center font-mono">
                    {f.ruleNumber}
                  </span>
                  <span>{f.factorName}</span>
                </div>
                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    f.promiseTerminology === 'Strong'
                      ? 'bg-emerald-100 text-emerald-800'
                      : f.promiseTerminology === 'Challenged'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-astro-secondary-surface text-astro-secondary-text'
                  }`}
                >
                  {f.promiseTerminology}
                </span>
              </div>
              <p className="text-xs leading-relaxed text-astro-secondary-text">
                {isHi ? f.findingTextHi : f.findingText}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Explain / Deep Reasoning Accordion */}
      <div className="border-t border-astro-border/80 pt-3">
        <button
          onClick={() => setIsDeepExpanded(!isDeepExpanded)}
          className="flex items-center justify-between w-full text-left text-xs font-bold text-astro-primary hover:text-[#0E3534] py-1"
        >
          <span className="flex items-center gap-1.5">
            <HelpCircle className="w-4 h-4 text-astro-accent" />
            {isHi ? 'गहन शास्त्रीय व्याख्या एवं तर्क (Explain Mode)' : 'Deep Classical Reasoning & Explanations'}
          </span>
          {isDeepExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {isDeepExpanded && (
          <div className="mt-3 p-4 bg-[#FAF6ED] rounded-xl border border-astro-border space-y-3 text-xs text-astro-secondary-text animate-fade-in">
            <div>
              <strong className="text-astro-primary block mb-1">
                {isHi ? 'सकारात्मक सहायक तत्व:' : 'Supporting Confluences:'}
              </strong>
              <p>{isHi ? assessment.deepReasoning.supportSummaryHi : assessment.deepReasoning.supportSummary}</p>
            </div>
            <div>
              <strong className="text-astro-primary block mb-1">
                {isHi ? 'बाधा/सतर्कता कारक:' : 'Contradicting & Cautionary Factors:'}
              </strong>
              <p>{isHi ? assessment.deepReasoning.challengeSummaryHi : assessment.deepReasoning.challengeSummary}</p>
            </div>
            <div>
              <strong className="text-astro-primary block mb-1">
                {isHi ? 'समग्र निर्णय विश्लेषण:' : 'Synthesized Verdict Analysis:'}
              </strong>
              <p>{isHi ? assessment.deepReasoning.netVerdictExplanationHi : assessment.deepReasoning.netVerdictExplanation}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
